"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useLocale } from "next-intl";
import { ToteIcon, type IconWeight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getCartItemCount } from "@/app/actions/cart";
import { cn } from "@/lib/utils";

interface CartButtonProps {
  /** Initial count from server (optional, for SSR) */
  initialCount?: number;
  /** Visual variant to match other navbar buttons */
  variant?: "default" | "ghost";
  className?: string;
  /** 내부 아이콘 크기 등 override (기본 size-4) */
  iconClassName?: string;
  /** phosphor 아이콘 굵기 (기본 regular). 데스크탑 등 옆 아이콘 굵기에 맞출 때 사용 */
  weight?: IconWeight;
}

export function CartButton({
  initialCount = 0,
  variant = "default",
  className,
  iconClassName,
  weight,
}: CartButtonProps) {
  const router = useRouter();
  const locale = useLocale();
  const [count, setCount] = useState(initialCount);

  // Fetch cart count on mount
  useEffect(() => {
    const fetchCount = async () => {
      const serverCount = await getCartItemCount();
      setCount(serverCount);
    };
    fetchCount();
  }, []);

  // Listen for cart updates (custom event)
  useEffect(() => {
    const handleCartUpdate = (event: CustomEvent<{ count: number }>) => {
      setCount(event.detail.count);
    };

    window.addEventListener("cart-updated" as any, handleCartUpdate);
    return () => {
      window.removeEventListener("cart-updated" as any, handleCartUpdate);
    };
  }, []);

  const handleClick = () => {
    router.push(`/${locale}/cart`);
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      className={cn(
        "relative h-10 w-10 rounded-full px-0 flex items-center justify-center",
        variant === "default" && "bg-gray-100 hover:bg-gray-200",
        variant === "ghost" && "hover:bg-gray-100",
        className,
      )}
      onClick={handleClick}
      aria-label={`Shopping cart${count > 0 ? `, ${count} items` : ""}`}
    >
      <ToteIcon weight={weight} className={cn("size-4", iconClassName)} />
      {count > 0 && (
        <Badge
          variant="destructive"
          className="absolute -top-1 -right-2 h-5 min-w-5 px-1.5 flex items-center justify-center text-xs font-bold"
        >
          {count > 99 ? "99+" : count}
        </Badge>
      )}
    </Button>
  );
}

/**
 * Dispatch cart update event to refresh all CartButton instances
 */
export function dispatchCartUpdate(count: number) {
  window.dispatchEvent(new CustomEvent("cart-updated", { detail: { count } }));
}
